import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { getUser, updateUser, uploadProfileImage } from '../../services/userService';
import { getSchools } from '../../services/schoolService';
import { useAuth } from '../../context/AuthContext';
import { getDashboardPath } from '../../App';
import Loader from '../common/Loader';
import ProfileImageUpload from '../common/ProfileImageUpload';
import DatePicker from '../common/DatePicker';
import StateCitySelect from '../org/StateCitySelect';
import { formatMMDDYYYY } from '../../utils/dateUtils';

const ROLES = [
  { id: 1, name: 'SPED Director' },
  { id: 2, name: 'SPED Teacher' },
];
const TEACHER_ROLE_ID = 2;

const inputCls =
  'w-full px-3.5 py-2.5 rounded-lg bg-gray-50 dark:bg-slate-700/60 border border-gray-300 dark:border-slate-600 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-colors text-sm';

function Section({ title, children }) {
  return (
    <div className="bg-white dark:bg-slate-800/40 border border-gray-200 dark:border-slate-700 rounded-xl p-4 sm:p-5 space-y-4">
      <h3 className="text-gray-900 dark:text-white font-semibold text-sm">{title}</h3>
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1.5">{label}</label>
      {children}
    </div>
  );
}

function ReadOnlyField({ label, value }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1.5">{label}</label>
      <p className="px-3.5 py-2.5 rounded-lg bg-gray-100 dark:bg-slate-700/40 border border-gray-200 dark:border-slate-600 text-gray-900 dark:text-white text-sm">
        {value || '—'}
      </p>
    </div>
  );
}

export default function EditUser() {
  const { id } = useParams();
  const navigate = useNavigate();
  const locationRef = useRef(null);
  const { user: currentUser } = useAuth();
  const isDirector = currentUser?.role === 1;
  const isSelf = Number(id) === currentUser?.id;
  const isTeacherSelfEdit = isSelf && currentUser?.role === 2;
  const [fetching, setFetching] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [hasProfileImage, setHasProfileImage] = useState(false);
  const [imageRefreshKey, setImageRefreshKey] = useState(0);
  const [profileImage, setProfileImage] = useState(null);
  const [imageUploading, setImageUploading] = useState(false);
  const [schools, setSchools] = useState([]);
  const [schoolName, setSchoolName] = useState('');

  const [form, setForm] = useState({
    first_name: '',
    middle_name: '',
    last_name: '',
    password: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    zipcode: '',
    mobile: '',
    role: '',
    school_id: '',
    hire_date: '',
    termination_date: '',
    is_active: '',
  });

  const set = (field) => (e) => {
    const value = e.target.value;
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'role' && parseInt(value, 10) !== TEACHER_ROLE_ID) {
        next.school_id = '';
      }
      return next;
    });
  };

  useEffect(() => {
    if (!isDirector && !isSelf) {
      navigate(getDashboardPath(currentUser?.role), { replace: true });
    }
  }, [isDirector, isSelf, navigate, currentUser?.role]);

  useEffect(() => {
    if (isDirector) {
      getSchools({ page: 1, limit: 100, status: 'active' })
        .then(({ data }) => setSchools(data.data?.schools || []))
        .catch(() => {});
    }
  }, [isDirector]);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const { data } = await getUser(id);
        const u = data.data;
        setForm({
          first_name: u.first_name || '',
          middle_name: u.middle_name || '',
          last_name: u.last_name || '',
          password: '',
          address_line1: u.address_line1 || '',
          address_line2: u.address_line2 || '',
          city: u.city || '',
          state: u.state || '',
          zipcode: u.zipcode || '',
          mobile: u.mobile || '',
          role: u.role || '',
          school_id: u.school_id ? String(u.school_id) : '',
          hire_date: u.hire_date || '',
          termination_date: u.termination_date || '',
          is_active: u.is_active || 'inactive',
        });
        setHasProfileImage(!!u.has_profile_image);
        setSchoolName(u.school_name || '');
      } catch {
        setError('Failed to load user data.');
      } finally {
        setFetching(false);
      }
    };
    fetchUser();
  }, [id]);

  const handleImageSelect = async (file) => {
    setProfileImage(file);
    setImageUploading(true);
    try {
      await uploadProfileImage(id, file);
      setHasProfileImage(true);
      setImageRefreshKey((k) => k + 1);
      setProfileImage(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to upload profile image.');
    } finally {
      setImageUploading(false);
    }
  };

  const handleRemoveImage = () => {
    setHasProfileImage(false);
    setProfileImage(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (parseInt(form.role, 10) === TEACHER_ROLE_ID && !form.school_id && !isTeacherSelfEdit) {
      setError('School is required for SPED Teachers.');
      return;
    }
    if ((form.state || form.city) && !locationRef.current?.validate()) {
      setError('Please select state and city from the dropdown.');
      return;
    }
    setLoading(true);

    const payload = { ...form };
    if (payload.zipcode) payload.zipcode = parseInt(payload.zipcode);
    if (payload.mobile) payload.mobile = parseInt(payload.mobile);
    if (payload.role) payload.role = parseInt(payload.role);
    if (payload.school_id) payload.school_id = parseInt(payload.school_id, 10);
    else if (parseInt(form.role, 10) !== TEACHER_ROLE_ID) payload.school_id = null;

    if (isTeacherSelfEdit) {
      delete payload.role;
      delete payload.school_id;
      delete payload.hire_date;
      delete payload.termination_date;
      delete payload.is_active;
    }
    delete payload.email;
    if (!payload.password) delete payload.password;
    Object.keys(payload).forEach((k) => {
      if (payload[k] === '' || payload[k] === null) delete payload[k];
    });

    try {
      await updateUser(id, payload);
      navigate(currentUser?.role === 2 ? getDashboardPath(2) : '/users');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update user.');
    } finally {
      setLoading(false);
    }
  };

  if (fetching) return <Loader text="Loading user data..." />;

  return (
    <div className="mx-auto w-full min-w-0 max-w-[min(100%,64rem)] space-y-5">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Link
          to={isTeacherSelfEdit ? getDashboardPath(2) : '/users'}
          className="p-2 rounded-xl text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </Link>
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
            {isTeacherSelfEdit ? 'Edit My Profile' : 'Edit User'}
          </h1>
          <p className="text-gray-500 dark:text-slate-400 text-sm">
            {isTeacherSelfEdit ? 'Update your personal information' : 'Update user information'}
          </p>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <div className="px-4 py-3 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 text-sm">
            {error}
          </div>
        )}

        <Section title="Profile Photo">
          <div className="relative">
            <ProfileImageUpload
              hasProfileImage={hasProfileImage}
              entityType="user"
              entityId={Number(id)}
              imageRefreshKey={imageRefreshKey}
              initials={`${form.first_name?.[0] || ''}${form.last_name?.[0] || ''}`.toUpperCase() || '??'}
              previewFile={profileImage}
              onFileSelect={handleImageSelect}
              onRemove={handleRemoveImage}
            />
            {imageUploading && (
              <div className="absolute inset-0 flex items-center justify-center bg-white/60 dark:bg-slate-800/60 rounded-xl">
                <div className="flex items-center gap-2 text-sm text-indigo-600 dark:text-indigo-400 font-medium">
                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                  </svg>
                  Uploading...
                </div>
              </div>
            )}
          </div>
        </Section>

        <Section title="Basic Information">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Field label="First Name *">
              <input
                type="text"
                required
                value={form.first_name}
                onChange={set('first_name')}
                placeholder="John"
                className={inputCls}
              />
            </Field>
            <Field label="Middle Name">
              <input
                type="text"
                value={form.middle_name}
                onChange={set('middle_name')}
                placeholder="K"
                className={inputCls}
              />
            </Field>
            <Field label="Last Name *">
              <input
                type="text"
                required
                value={form.last_name}
                onChange={set('last_name')}
                placeholder="Doe"
                className={inputCls}
              />
            </Field>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="New Password (leave blank to keep)">
              <input
                type="password"
                minLength={6}
                value={form.password}
                onChange={set('password')}
                placeholder="••••••••"
                className={inputCls}
              />
            </Field>
            <Field label="Mobile">
              <input
                type="text"
                value={form.mobile}
                onChange={set('mobile')}
                placeholder="8015551234"
                className={inputCls}
              />
            </Field>
          </div>
        </Section>

        <Section title="Role & Status">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {isTeacherSelfEdit ? (
              <>
                <ReadOnlyField
                  label="Role"
                  value={ROLES.find((r) => r.id === parseInt(form.role, 10))?.name}
                />
                <ReadOnlyField label="School" value={schoolName || '—'} />
                <ReadOnlyField label="Hire Date" value={formatMMDDYYYY(form.hire_date) || '—'} />
                <ReadOnlyField label="Status" value={form.is_active} />
                <ReadOnlyField label="Termination Date" value={formatMMDDYYYY(form.termination_date) || '—'} />
              </>
            ) : (
              <>
            <Field label="Role">
              <select value={form.role} onChange={set('role')} className={inputCls}>
                <option value="">Select role</option>
                {ROLES.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                  </option>
                ))}
              </select>
            </Field>
            {parseInt(form.role, 10) === TEACHER_ROLE_ID && (
              <Field label="School *">
                <select required value={form.school_id} onChange={set('school_id')} className={inputCls}>
                  <option value="">Select school</option>
                  {schools.map((s) => (
                    <option key={s.id} value={s.id}>{s.school_name}</option>
                  ))}
                </select>
              </Field>
            )}
            <Field label="Hire Date">
              <DatePicker
                value={form.hire_date}
                onChange={(v) => setForm((prev) => ({ ...prev, hire_date: v }))}
                outputFormat="YYYY-MM-DD"
                className={inputCls}
                aria-label="Hire Date"
              />
            </Field>
            <Field label="Status">
              <select value={form.is_active} onChange={set('is_active')} className={inputCls}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="suspended">Suspended</option>
              </select>
            </Field>
            <Field label="Termination Date">
              <DatePicker
                value={form.termination_date}
                onChange={(v) => setForm((prev) => ({ ...prev, termination_date: v }))}
                outputFormat="YYYY-MM-DD"
                className={inputCls}
                aria-label="Termination Date"
              />
            </Field>
              </>
            )}
          </div>
        </Section>

        <Section title="Address (Optional)">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Address Line 1">
              <input
                type="text"
                value={form.address_line1}
                onChange={set('address_line1')}
                placeholder="123 Main St"
                className={inputCls}
              />
            </Field>
            <Field label="Address Line 2">
              <input
                type="text"
                value={form.address_line2}
                onChange={set('address_line2')}
                placeholder="Suite 200"
                className={inputCls}
              />
            </Field>
            <StateCitySelect
              ref={locationRef}
              inputNamePrefix="athenian-user"
              stateCode={form.state}
              city={form.city}
              onStateChange={(value) => setForm((prev) => ({ ...prev, state: value, city: '' }))}
              onCityChange={(value) => setForm((prev) => ({ ...prev, city: value }))}
            />
            <Field label="Zipcode">
              <input
                type="text"
                value={form.zipcode}
                onChange={set('zipcode')}
                placeholder="84101"
                className={inputCls}
              />
            </Field>
          </div>
        </Section>

        <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={() => window.history.back()}
            className="w-full sm:w-auto px-5 py-2.5 rounded-xl border border-gray-300 dark:border-slate-700 text-gray-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white hover:border-gray-400 dark:hover:border-slate-600 text-sm font-medium transition-colors text-center"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="w-full sm:w-auto px-6 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white text-sm font-semibold transition-colors shadow-lg shadow-indigo-500/20 text-center"
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
}